import { useEffect, useMemo, useState } from 'react'

import { CepIntegration, PayloadCepIntegration } from 'common/api'
import ConfirmationModal from 'common/components/confirmationModal'
import GridContainer from 'common/components/grid/GridContainer'
import LoadingOverlay from 'common/components/LoadingOverlay'
import { useAppSelector } from 'common/hooks/redux'
import { DistributionChannel } from 'common/interfaces/projects'

import useDeleteCepIntegration from '../../api/mutations/useDeleteCepIntegration'
import useUpdateCepIntegration from '../../api/mutations/useUpdateCepIntegration'
import useGetCepIntegrationTemplatesQuery, {
  CepTemplateFormatted,
} from '../../api/queries/useGetCepIntegrationTemplatesQuery'
import useGetIntegrationByIdQuery from '../../api/queries/useGetIntegrationByIdQuery'

import CreateCepIntegrationModal from './createCepIntegrationWidget/CreateCepIntegrationModal'
import AccountCard from './AccountCard'

type Props = {
  accounts: CepIntegration[]
}

const AccountsGrid = ({ accounts }: Props) => {
  let cepTemplate: CepTemplateFormatted | undefined = undefined
  const accountId = useAppSelector((state) => state.authStates.accountId)
  const { mutate: deleteCepIntegration, isPending: isDeletingCepIntegration } =
    useDeleteCepIntegration()
  const { mutate: updateCepIntegration, isPending: isUpdatingCepIntegration } =
    useUpdateCepIntegration()
  const { data: cepTemplates } = useGetCepIntegrationTemplatesQuery(
    accountId,
    true
  )
  const [selectedIntegration, setSelectedIntegration] = useState<
    CepIntegration | undefined
  >(undefined)
  const [integrationToDelete, setIntegrationToDelete] = useState<
    CepIntegration | undefined
  >(undefined)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const [isCreateWidgetVisible, setIsCreateWidgetVisible] =
    useState<boolean>(false)

  const { data: integration, isFetching } = useGetIntegrationByIdQuery(
    selectedIntegration?._id
  )

  useEffect(() => {
    if (integration?.credentials) {
      setSelectedIntegration((prev) =>
        prev
          ? {
              ...prev,
              credentials: integration.credentials,
            }
          : prev
      )
    }
  }, [integration])

  cepTemplate = useMemo(() => {
    if (!cepTemplates || !selectedIntegration || !integration) {
      return undefined
    }
    return cepTemplates.find(
      (cep) => cep.cepName === selectedIntegration.cep_name
    )
  }, [cepTemplates, selectedIntegration, integration])

  return (
    <GridContainer data-testid="accountsGridList">
      {accounts.map((cepIntegration) => (
        <AccountCard
          key={cepIntegration._id}
          name={cepIntegration.name}
          cepName={cepIntegration.cep_name}
          createdAt={cepIntegration.created_at}
          onDelete={() => {
            setIsDeleteModalVisible(true)
            setIntegrationToDelete(cepIntegration)
          }}
          distributionChannel={
            cepIntegration.distribution_channel as DistributionChannel
          }
          onUpdate={() => {
            if (selectedIntegration?._id !== cepIntegration._id) {
              setSelectedIntegration(cepIntegration)
            }
            setIsCreateWidgetVisible(true)
          }}
        />
      ))}
      <ConfirmationModal
        data-cy="delete-confirmation-modal"
        data-testid="delete-confirmation-modal"
        title="Delete this integration?"
        confirmationText="This action cannot be undone."
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        open={isDeleteModalVisible}
        hasConfirmationSlider
        onConfirm={() => {
          deleteCepIntegration(integrationToDelete?._id!)
          setIsDeleteModalVisible(false)
          setIntegrationToDelete(undefined)
        }}
        onCancel={() => {
          setIsDeleteModalVisible(false)
          setIntegrationToDelete(undefined)
        }}
      />
      {cepTemplate && (
        <CreateCepIntegrationModal
          cep={cepTemplate}
          integrationToUpdate={selectedIntegration}
          isCreateWidgetVisible={isCreateWidgetVisible}
          onClose={() => {
            setIsCreateWidgetVisible(false)
          }}
          onSubmit={async (payload: PayloadCepIntegration) => {
            updateCepIntegration({
              payload,
              integrationId: selectedIntegration?._id!,
            })
            setSelectedIntegration(undefined)
          }}
        />
      )}
      <LoadingOverlay
        isLoading={
          isFetching || isUpdatingCepIntegration || isDeletingCepIntegration
        }
        phrases={['Loading ...']}
      />
    </GridContainer>
  )
}

export default AccountsGrid
